/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { SETTINGS_GROUPS } from '@/lib/settingsNav';

/**
 * The places in Vellum itself -- screens, their tabs, the dashboard's cards and
 * each settings page -- so search finds "signing volume" or "out of office" as
 * well as the agreements and help that mention them.
 *
 * Words only: a place is found by its own name, where it sits, and the words a
 * person would use for it. Nothing here is data; the server answers for that,
 * and only with what the person may open.
 */

/** Where a place opens. A small closed set, so every one is a real route. */
export type Destination =
  | { to: '/agreements'; search?: { kind?: 'packet' | 'regular' } }
  | {
      to:
        | '/dashboard'
        | '/records'
        | '/workflows'
        | '/packets'
        | '/deadlines'
        | '/counterparties'
        | '/import'
        | '/help'
        | (typeof SETTINGS_GROUPS)[number]['sections'][number]['to'];
    };

export interface Place {
  id: string;
  /** What it is called on screen. */
  title: string;
  /** Where it sits: `Dashboard › Performance`. */
  where: string;
  /** Other words somebody might type for it. */
  words: readonly string[];
  open: Destination;
  /** A permission it needs, where the screen does; the server decides again. */
  needs?: string | undefined;
}

const DASHBOARD = 'Dashboard';

/** One of the dashboard's cards. */
function card(id: string, title: string, where: string, words: readonly string[]): Place {
  return { id: `card-${id}`, title, where, words, open: { to: '/dashboard' } };
}

/** The dashboard's cards, by the names they carry. */
const CARDS: readonly Place[] = [
  card('glance', 'At a glance', DASHBOARD, ['summary', 'overview', 'counts', 'totals']),
  card('signed', 'Signed', `${DASHBOARD} › At a glance`, [
    'completed',
    'executed',
    'done',
  ]),
  card('in-progress', 'In progress', `${DASHBOARD} › At a glance`, [
    'active',
    'open',
    'ongoing',
  ]),
  card('drafts', 'Drafts not sent', `${DASHBOARD} › At a glance`, ['draft', 'unsent']),
  card('expiring', 'Expiring soon', `${DASHBOARD} › At a glance`, [
    'expiry',
    'renewal',
    'ending',
  ]),
  card('volume', 'Signing volume', `${DASHBOARD} › Performance`, [
    'chart',
    'signed per week',
    'trend',
  ]),
  card('time-to-sign', 'Average time to signature', `${DASHBOARD} › Performance`, [
    'speed',
    'how long',
    'turnaround',
    'cycle time',
  ]),
  card('workflows-in-use', 'Workflows in use', `${DASHBOARD} › Performance`, [
    'templates used',
  ]),
  card('team', 'Who worked on what', DASHBOARD, [
    'owners',
    'people',
    'workload',
    'per person',
  ]),
  card('finished', 'Recently finished', DASHBOARD, [
    'latest signed',
    'filed',
    'completed',
  ]),
  card('status', 'Where everything is right now', DASHBOARD, [
    'pipeline',
    'stages',
    'status',
  ]),
  card('waiting', 'Waiting on you', DASHBOARD, ['to do', 'my turn', 'action', 'approve']),
  card('quarter', 'Agreements signed each quarter', `${DASHBOARD} › Add insight`, [
    'quarterly',
    'per quarter',
  ]),
  card('cycle', 'Time to signature by workflow', `${DASHBOARD} › Add insight`, [
    'cycle time',
    'by template',
  ]),
  card('dept', 'Agreements by team', `${DASHBOARD} › Add insight`, [
    'department',
    'share',
  ]),
  card('deck', 'Leadership deck', `${DASHBOARD} › Export`, [
    'email me',
    'powerpoint',
    'slides',
    'pptx',
    'export',
    'report',
  ]),
  card('weekly', 'Weekly email to leadership', `${DASHBOARD} › Export`, [
    'schedule',
    'report',
    'leadership email',
  ]),
];

/** The screens and their tabs. */
const SCREENS: readonly Place[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    where: 'Home',
    words: ['home', 'insights', 'reports', 'performance'],
    open: { to: '/dashboard' },
  },
  {
    id: 'agreements',
    title: 'Agreements in progress',
    where: 'Agreements',
    words: ['contracts', 'drafts', 'in approval', 'out for signature', 'documents'],
    open: { to: '/agreements' },
  },
  {
    id: 'completed',
    title: 'Completed agreements',
    where: 'Agreements › Completed',
    words: ['records', 'signed', 'filed', 'archive', 'executed'],
    open: { to: '/records' },
  },
  {
    id: 'packets-running',
    title: 'Packets in progress',
    where: 'Agreements › Packets',
    words: ['packet', 'onboarding', 'next step', 'checklist'],
    open: { to: '/agreements', search: { kind: 'packet' } },
  },
  {
    id: 'templates',
    title: 'Agreement templates',
    where: 'Templates',
    words: ['workflows', 'template', 'documents', 'new agreement'],
    open: { to: '/workflows' },
  },
  {
    id: 'packet-templates',
    title: 'Packet templates',
    where: 'Templates › Packets',
    words: ['packets', 'bundle', 'start a packet', 'onboarding'],
    open: { to: '/packets' },
  },
  {
    id: 'deadlines',
    title: 'Deadlines',
    where: 'Deadlines',
    words: ['expiry', 'renewals', 'dates', 'calendar', 'obligations'],
    open: { to: '/deadlines' },
  },
  {
    id: 'counterparties',
    title: 'Counterparties',
    where: 'Counterparties',
    words: ['companies', 'vendors', 'clients', 'people we sign with'],
    open: { to: '/counterparties' },
  },
  {
    id: 'import',
    title: 'Import agreements',
    where: 'Import',
    words: ['upload', 'bring in', 'existing contracts', 'ironclad'],
    open: { to: '/import' },
    needs: 'import:run',
  },
  {
    id: 'help',
    title: 'Help',
    where: 'Help',
    words: ['guide', 'how to', 'support', 'faq'],
    open: { to: '/help' },
  },
];

/** Other words for each settings page, by its address. */
const SETTINGS_WORDS: Record<string, readonly string[]> = {
  '/settings/profile': ['account', 'name', 'me', 'theme', 'dark mode'],
  '/settings/notifications': ['email', 'alerts', 'reminders', 'notices', 'slack'],
  '/settings/out-of-office': [
    'away',
    'holiday',
    'vacation',
    'leave',
    'cover',
    'delegate',
  ],
  '/settings/people': ['users', 'roles', 'colleagues', 'team members', 'leaver'],
  '/settings/groups': ['roles', 'directory', 'entra', 'access'],
  '/settings/company': ['logo', 'letterhead', 'address', 'organization', 'branding'],
  '/settings/integrations': ['dropbox sign', 'slack', 'sharepoint', 'drive', 'email'],
  '/settings/audit': ['audit log', 'history', 'who did this', 'compliance', 'export'],
};

const SETTINGS: readonly Place[] = SETTINGS_GROUPS.flatMap((group) =>
  group.sections.map((section) => ({
    id: `settings-${section.to}`,
    title: section.label,
    where: `Settings › ${group.heading}`,
    words: SETTINGS_WORDS[section.to] ?? [],
    open: { to: section.to },
    needs: section.needs,
  })),
);

export const PLACES: readonly Place[] = [...SCREENS, ...CARDS, ...SETTINGS];

/** Lower-case words, without punctuation. */
function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * The places that answer a search: every word typed appears in the place's
 * title, where it sits, or its other words. Closest first -- a title that
 * starts with what was typed, then one that contains it -- and never a place
 * this person's permissions do not open.
 */
export function placesMatching(
  term: string,
  permissions: ReadonlySet<string> | undefined,
  most = 5,
): Place[] {
  const typed = wordsOf(term);
  if (typed.length === 0) return [];
  const phrase = typed.join(' ');

  const scored = PLACES.filter(
    (place) => place.needs === undefined || (permissions?.has(place.needs) ?? false),
  ).flatMap((place) => {
    const title = wordsOf(place.title).join(' ');
    const known = wordsOf([place.title, place.where, ...place.words].join(' '));
    // Each word typed starts one of the place's words: "sign" finds "Signing".
    if (!typed.every((word) => known.some((each) => each.startsWith(word)))) return [];
    const score = title.startsWith(phrase) ? 0 : title.includes(phrase) ? 1 : 2;
    return [{ place, score }];
  });

  return scored
    .sort((a, b) => a.score - b.score || a.place.title.localeCompare(b.place.title))
    .slice(0, most)
    .map(({ place }) => place);
}
